import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import useFetch from '../../../services/useFetch.js';
import PostData from '../../../services/PostData.js';
import AppDialog from '../../ui/AppDialog';
import AddIngredient from './bulkCustomItem/AddIngredients.jsx';
import AddMenuItems from './bulkCustomItem/AddMenuItems.jsx';
import SnackbarEx from '../../Helpers/SnackbarEx';

const UoM = ['oz', 'floz', 'mL', 'quarts', 'grams', 'cup', 'liter', 'units', 'none'];

export default function CustomItemDialog({ close, open = true }) {
  const { data: categories } = useFetch('api/menuCategories', false);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ color: '', message: '', open: false });
  const [form, setForm] = useState({
    name: '',
    category: 'Beverages - Coffee',
    size: '',
    sizeUoM: 'oz',
    type: 'customIngredient',
    ageFactor: 0,
    ingredients: [],
    menuItems: [],
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleIngredients = (value) => {
    if (Array.isArray(value)) {
      setForm((prev) => ({ ...prev, ingredients: value }));
    }
  };

  const updateMenuItems = (value) => {
    if (Array.isArray(value)) setForm((prev) => ({ ...prev, menuItems: value }));
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      setSnack({ color: 'error', message: 'Item name is required.', open: true });
      return;
    }
    if (!form.size) {
      setSnack({ color: 'error', message: 'Batch size is required.', open: true });
      return;
    }
    if (!form.ingredients?.some((ing) => ing?.id != null && ing?.name)) {
      setSnack({ color: 'error', message: 'Add at least one ingredient.', open: true });
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      ingredients: form.ingredients.filter((ing) => ing?.id != null && ing?.name),
      menuItems: form.menuItems.filter(
        (m) => m?.menuId != null && m?.menuAmount != null && m?.menuAmount !== '' && m?.menuUoM,
      ),
    };
    const { success } = await PostData(payload, 'api/custom-menu-item', false);
    setSubmitting(false);
    if (success) {
      close('success');
    } else {
      setSnack({ color: 'error', message: 'Could not save recipe item.', open: true });
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={() => close(false)}
      title="New recipe item"
      subtitle="Batch / specialty items built from raw ingredients"
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={() => close(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save item'}
          </Button>
        </>
      }
    >
      <Stack spacing={2.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Details</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Item name"
            required
            fullWidth
            placeholder="Batch brew carafe, house syrup…"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />
          <TextField
            select
            label="Category"
            fullWidth
            value={form.category}
            onChange={(e) => setField('category', e.target.value)}
          >
            {(categories || ['Beverages - Coffee']).map((c) => (
              <MenuItem key={typeof c === 'string' ? c : c.category || String(c)} value={typeof c === 'string' ? c : c.category || c}>
                {typeof c === 'string' ? c : c.category || c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Type"
            fullWidth
            value={form.type}
            onChange={(e) => setField('type', e.target.value)}
          >
            <MenuItem value="customIngredient">Custom ingredient</MenuItem>
            <MenuItem value="bulkMenuItem">Bulk menu item</MenuItem>
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Batch size"
            type="number"
            required
            fullWidth
            value={form.size}
            onChange={(e) => setField('size', e.target.value)}
          />
          <TextField
            select
            label="Size UoM"
            fullWidth
            value={form.sizeUoM}
            onChange={(e) => setField('sizeUoM', e.target.value)}
          >
            {UoM.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </TextField>
          <TextField
            label="Aging (days)"
            type="number"
            fullWidth
            value={form.ageFactor}
            onChange={(e) => setField('ageFactor', e.target.value === '' ? 0 : Number(e.target.value))}
          />
        </Stack>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Ingredients</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Raw inventory used to produce one batch of this item.
          </Typography>
          <AddIngredient onDataChange={handleIngredients} />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Used in products</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Finished products that draw from this recipe item (optional).
          </Typography>
          <AddMenuItems
            updateMenuItems={updateMenuItems}
            itemType={form.type}
            customSize={form.sizeUoM}
          />
        </Box>
      </Stack>

      {snack.open && (
        <SnackbarEx
          color={snack.color}
          message={snack.message}
          open={snack.open}
          close={() => setSnack({ color: '', message: '', open: false })}
        />
      )}
    </AppDialog>
  );
}
